import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";

const getAuthHeaders = (getState, { isFormData = false } = {}) => {
  const { loginToken } = getState();
  const authToken = loginToken?.response?.token;
  const headers = {
    Authorization: `Bearer ${authToken}`,
  };
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }
  return { headers };
};

export const fetchWidgetSettingsAdminByGroup = createAsyncThunk(
  "widgetSettingsGroup/fetchAdmin",
  async ({ groupId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${ROOT_URL}/widget-settings/admin/group/${groupId}`,
        getAuthHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const fetchWidgetSettingsByGroup = createAsyncThunk(
  "widgetSettingsGroup/fetchPublic",
  async ({ groupId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${ROOT_URL}/widget-settings/group/${groupId}`,
        getAuthHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const createWidgetSettingsByGroup = createAsyncThunk(
  "widgetSettingsGroup/create",
  async ({ groupId, body }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/widget-settings/group/${groupId}`,
        body,
        getAuthHeaders(getState, { isFormData: body instanceof FormData }),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateWidgetSettingsByGroup = createAsyncThunk(
  "widgetSettingsGroup/update",
  async ({ groupId, body }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${ROOT_URL}/widget-settings/group/${groupId}`,
        body,
        getAuthHeaders(getState, { isFormData: body instanceof FormData }),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const initialState = {
  settings: null,
  loading: false,
  saving: false,
  error: null,
  configured: false,
};

const widgetSettingsGroupApi = createSlice({
  name: "widgetSettingsGroup",
  initialState,
  reducers: {
    clearWidgetSettingsGroup: (state) => {
      state.settings = null;
      state.error = null;
      state.configured = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWidgetSettingsAdminByGroup.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchWidgetSettingsAdminByGroup.fulfilled, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.loading = false;
        }
        state.settings = action.payload?.data ?? null;
        state.configured = Boolean(action.payload?.configured);
      })
      .addCase(fetchWidgetSettingsAdminByGroup.rejected, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.loading = false;
        }
        state.error = action.payload;
        if (!action.meta.arg?.silent) {
          state.settings = null;
          state.configured = false;
        }
      })
      .addCase(createWidgetSettingsByGroup.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createWidgetSettingsByGroup.fulfilled, (state, action) => {
        state.saving = false;
        state.settings = action.payload?.data ?? null;
        state.configured = action.payload?.configured ?? true;
      })
      .addCase(createWidgetSettingsByGroup.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateWidgetSettingsByGroup.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateWidgetSettingsByGroup.fulfilled, (state, action) => {
        state.saving = false;
        state.settings = action.payload?.data ?? null;
        state.configured = action.payload?.configured ?? true;
      })
      .addCase(updateWidgetSettingsByGroup.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export const { clearWidgetSettingsGroup } = widgetSettingsGroupApi.actions;
export default widgetSettingsGroupApi.reducer;
